import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Mail, Calendar, CheckCircle, Trash2, XCircle, ClipboardList, ChevronDown, ChevronUp, MapPin, DollarSign, Heart, Search, X, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = '/api';

const AppointmentsTab = ({ 
  appointments, 
  appointmentsPage, 
  setAppointmentsPage, 
  appointmentsTotalPages, 
  fetchData,
  surveys = {}  // Survey data keyed by appointment_id
}) => {
  const [expandedSurveys, setExpandedSurveys] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [resendingEmail, setResendingEmail] = useState({});

  // Filter appointments based on search query
  const filteredAppointments = useMemo(() => {
    if (!searchQuery.trim()) return appointments;
    const query = searchQuery.toLowerCase();
    return appointments.filter(appt => 
      appt.lead_name?.toLowerCase().includes(query) ||
      appt.lead_email?.toLowerCase().includes(query) ||
      appt.date?.includes(query) ||
      appt.status?.toLowerCase().includes(query) ||
      appt.booking_type?.toLowerCase().includes(query)
    );
  }, [appointments, searchQuery]);

  const toggleSurvey = (apptId) => {
    setExpandedSurveys(prev => ({
      ...prev,
      [apptId]: !prev[apptId]
    }));
  };

  const handleResendConfirmation = async (appointmentId, leadEmail) => {
    const token = localStorage.getItem('admin_token');
    setResendingEmail(prev => ({ ...prev, [appointmentId + '_conf']: true }));
    try {
      await axios.post(
        `${API}/appointments/${appointmentId}/resend-confirmation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Confirmation email with Zoom link resent to ${leadEmail}`);
    } catch (error) {
      toast.error('Failed to resend confirmation email');
    } finally {
      setResendingEmail(prev => ({ ...prev, [appointmentId + '_conf']: false }));
    }
  };

  const handleResendPostCall = async (appointmentId, leadEmail) => {
    const token = localStorage.getItem('admin_token');
    setResendingEmail(prev => ({ ...prev, [appointmentId + '_post']: true }));
    try {
      await axios.post(
        `${API}/appointments/${appointmentId}/resend-post-call`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Post-call email with payment link resent to ${leadEmail}`);
    } catch (error) {
      toast.error('Failed to resend post-call email');
    } finally {
      setResendingEmail(prev => ({ ...prev, [appointmentId + '_post']: false }));
    }
  };

  const handleMarkComplete = async (appointmentId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.patch(
        `${API}/appointments/${appointmentId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Call marked as complete. Post-call email sent!');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark call as complete');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <h3 className="text-xl font-bold">Appointments</h3>
        
        {/* Search and Pagination */}
        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-64 pl-10 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] focus:border-transparent"
              data-testid="appointments-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAppointmentsPage(p => Math.max(1, p - 1))}
              disabled={appointmentsPage === 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-[#525252]">
              Page {appointmentsPage} of {appointmentsTotalPages}
            </span>
            <button
              onClick={() => setAppointmentsPage(p => Math.min(appointmentsTotalPages, p + 1))}
              disabled={appointmentsPage === appointmentsTotalPages}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search Results Count */}
      {searchQuery && (
        <p className="text-sm text-[#525252] mb-3">
          Found {filteredAppointments.length} result{filteredAppointments.length !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      <div className="space-y-4" data-testid="appointments-list">
        {filteredAppointments.map((appt) => {
          const survey = surveys[appt.id];
          const isExpanded = expandedSurveys[appt.id];
          
          return (
            <div key={appt.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">{appt.lead_name}</h3>
                  <div className="space-y-1 text-sm text-[#525252]">
                    <p className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                        appt.booking_type === 'next_steps_call' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {appt.booking_type === 'next_steps_call' ? 'Next Steps' : 'First Inquiry'}
                      </span>
                      {survey && (
                        <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">
                          Survey Completed
                        </span>
                      )}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {appt.lead_email}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {format(parseISO(appt.date), 'EEEE, MMMM d, yyyy')} at {appt.time} EST
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Status: <span className={`font-semibold ${
                        appt.status === 'completed' ? 'text-[#25D366]' :
                        appt.status === 'scheduled' ? 'text-[#0055FF]' :
                        'text-[#525252]'
                      }`}>{appt.status}</span>
                    </p>
                  </div>

                  {/* Survey Toggle Button */}
                  {survey && (
                    <button
                      onClick={() => toggleSurvey(appt.id)}
                      className="mt-3 flex items-center gap-2 text-sm text-[#FF4500] hover:text-[#E03E00] font-medium"
                      data-testid={`toggle-survey-${appt.id}`}
                    >
                      <ClipboardList className="w-4 h-4" />
                      {isExpanded ? 'Hide Survey Responses' : 'View Survey Responses'}
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}

                  {/* Survey Summary - Expandable */}
                  {survey && isExpanded && (
                    <div className="mt-4 bg-white rounded-lg p-4 border border-gray-200 space-y-4" data-testid={`survey-summary-${appt.id}`}>
                      <h4 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-[#FF4500]" />
                        Pre-Call Survey Responses
                      </h4>
                      
                      {/* Location */}
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-[#525252] mt-0.5" />
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold">Location</p>
                          <p className="text-sm">{survey.city}, {survey.province_state}, {survey.country}</p>
                        </div>
                      </div>

                      {/* Desire for Change */}
                      <div className="flex items-start gap-2">
                        <Heart className="w-4 h-4 text-[#525252] mt-0.5" />
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold">Desire for Change</p>
                          <div className="flex items-center gap-2">
                            <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#FF4500] rounded-full" 
                                style={{ width: `${(survey.desire_for_change || 5) * 10}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-bold text-[#FF4500]">{survey.desire_for_change || 5}/10</span>
                          </div>
                        </div>
                      </div>

                      {/* When Realized Change */}
                      {survey.when_realized_change && (
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold mb-1">When They Realized Change Was Needed</p>
                          <p className="text-sm bg-gray-50 p-2 rounded">{survey.when_realized_change}</p>
                        </div>
                      )}

                      {/* Changes Looking For */}
                      {survey.changes_looking_for && survey.changes_looking_for.length > 0 && (
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold mb-1">Changes Looking For</p>
                          <div className="flex flex-wrap gap-1">
                            {survey.changes_looking_for.map((change, idx) => (
                              <span key={idx} className="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                {change}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Money Needed */}
                      {(survey.money_needed_monthly || survey.money_needed_yearly) && (
                        <div className="flex items-start gap-2">
                          <DollarSign className="w-4 h-4 text-[#525252] mt-0.5" />
                          <div>
                            <p className="text-xs text-[#525252] uppercase font-semibold">Income Goals</p>
                            <p className="text-sm">
                              {survey.money_needed_monthly && `$${survey.money_needed_monthly}/month`}
                              {survey.money_needed_monthly && survey.money_needed_yearly && ' • '}
                              {survey.money_needed_yearly && `$${survey.money_needed_yearly}/year`}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Why Online Business */}
                      {survey.why_online_business && (
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold mb-1">Why Online Business</p>
                          <p className="text-sm bg-gray-50 p-2 rounded">{survey.why_online_business}</p>
                        </div>
                      )}

                      {/* Marketing Budget */}
                      {survey.marketing_budget && (
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold mb-1">Marketing Budget</p>
                          <span className="inline-block px-2 py-1 bg-green-50 text-green-700 rounded text-sm">
                            {survey.marketing_budget === 'other' ? survey.marketing_budget_other : survey.marketing_budget}
                          </span>
                        </div>
                      )}

                      {/* Readiness Level */}
                      {survey.readiness_level && (
                        <div>
                          <p className="text-xs text-[#525252] uppercase font-semibold mb-1">Readiness Level</p>
                          <span className={`inline-block px-2 py-1 rounded text-sm ${
                            survey.readiness_level.includes('Ready') 
                              ? 'bg-green-100 text-green-800' 
                              : survey.readiness_level.includes('curious')
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {survey.readiness_level}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {/* Resend Confirmation Email (for scheduled appointments) */}
                  {appt.status === 'scheduled' && (
                    <button
                      onClick={() => handleResendConfirmation(appt.id, appt.lead_email)}
                      disabled={resendingEmail[appt.id + '_conf']}
                      className="bg-blue-500 text-white px-3 py-2 rounded-lg hover:bg-blue-600 transition-all duration-300 font-semibold text-sm flex items-center gap-1 disabled:opacity-50"
                      title="Resend confirmation email with Zoom link"
                      data-testid={`resend-confirmation-${appt.id}`}
                    >
                      {resendingEmail[appt.id + '_conf'] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Resend Zoom Link
                    </button>
                  )}
                  
                  {/* Resend Post-Call Email (for completed appointments) */}
                  {appt.status === 'completed' && (
                    <button
                      onClick={() => handleResendPostCall(appt.id, appt.lead_email)}
                      disabled={resendingEmail[appt.id + '_post']}
                      className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 transition-all duration-300 font-semibold text-sm flex items-center gap-1 disabled:opacity-50"
                      title="Resend post-call email with payment link"
                      data-testid={`resend-post-call-${appt.id}`}
                    >
                      {resendingEmail[appt.id + '_post'] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Resend Payment Link
                    </button>
                  )}
                  
                  {appt.status === 'scheduled' && (
                    <>
                      <button
                        onClick={() => handleMarkComplete(appt.id)}
                        className="bg-[#25D366] text-white px-4 py-2 rounded-lg hover:bg-[#1EBE5A] transition-all duration-300 font-semibold text-sm"
                        data-testid={`mark-complete-${appt.id}`}
                      >
                        Mark Complete
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Cancel appointment for "${appt.lead_name}"? They will be able to book again.`)) return;
                          const token = localStorage.getItem('admin_token');
                          try {
                            await axios.post(`${API}/appointments/${appt.id}/cancel`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            toast.success('Appointment cancelled. Lead can book again.');
                            fetchData();
                          } catch (error) {
                            toast.error('Failed to cancel appointment');
                          }
                        }}
                        className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-all duration-300 font-semibold text-sm flex items-center gap-1"
                        title="Cancel appointment (lead can rebook)"
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel & Allow Rebook
                      </button>
                    </>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete appointment for "${appt.lead_name}"?`)) return;
                      const token = localStorage.getItem('admin_token');
                      try {
                        await axios.delete(`${API}/appointments/${appt.id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success('Appointment deleted');
                        fetchData();
                      } catch (error) {
                        toast.error('Failed to delete appointment');
                      }
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                    title="Delete appointment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {filteredAppointments.length === 0 && (
          <p className="text-center text-[#525252] py-8">
            {searchQuery ? 'No appointments match your search' : 'No appointments yet'}
          </p>
        )}
      </div>
    </div>
  );
};

export default AppointmentsTab;
